import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import AuthForm from "@/components/auth/AuthForm";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, LogIn, ArrowRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { posthog } from "@/lib/posthog";

// OAuth Account Exists Modal Component
function OAuthAccountExistsModal({ 
  isOpen, 
  onClose, 
  provider, 
  email, 
  onGoToLogin 
}: {
  isOpen: boolean;
  onClose: () => void;
  provider: string;
  email: string;
  onGoToLogin: () => void;
}) {
  const providerName = provider === 'google' ? 'Google' : provider === 'azure' ? 'Microsoft' : provider;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50">
              <LogIn className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-left">Account Already Exists</DialogTitle>
              <DialogDescription className="text-left">
                This email is already registered
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="rounded-lg bg-orange-50 p-4">
            <p className="text-sm text-orange-700">
              <strong>{email}</strong> already has a Bloom account.
            </p>
            <p className="text-sm text-orange-600 mt-2">
              Please use the login page to sign in with your {providerName} account or email and password.
            </p>
          </div>
          
          <div className="space-y-3">
            <Button onClick={onGoToLogin} className="w-full" size="lg">
              <LogIn className="mr-2 h-4 w-4" />
              Sign In Instead
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <Button onClick={onClose} variant="outline" className="w-full">
              <X className="mr-2 h-4 w-4" />
              Back to Register
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 text-center">
            You can sign in with {providerName} or your email and password
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Register() {
  const { register, user, error, isLoading, clearError, setOAuthContext } = useAuth();
  const [_, navigate] = useLocation();
  const [oauthError, setOauthError] = useState<{provider: string; email: string} | null>(null);
  
  // Set OAuth context to registration when this page loads
  useEffect(() => {
    console.log("Register page - Setting OAuth context to 'register'");
    setOAuthContext('register');
    
    // Reset to login context when leaving the page
    return () => {
      console.log("Register page - Resetting OAuth context to 'login'");
      setOAuthContext('login');
    };
  }, [setOAuthContext]);
  
  // Handle OAuth-specific errors
  useEffect(() => {
    if (error && error.startsWith('OAUTH_ACCOUNT_EXISTS:')) {
      const parts = error.split(':');
      if (parts.length === 3) {
        setOauthError({
          provider: parts[1],
          email: parts[2]
        });
      }
    } else {
      setOauthError(null);
    }
  }, [error]);
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);
  
  const handleRegister = (username: string, password: string, name: string, email: string, school?: string) => {
    // Identify user in PostHog before registration
    posthog.identify(email, {
      email: email,
      name: name,
      username: username,
      school: school,
      registration_method: 'email',
      $email: email,
      $name: name || email.split('@')[0],
    });
    
    register(username, password, name, email, school);
  };
  
  const handleGoToLogin = () => {
    setOauthError(null);
    clearError();
    navigate("/login");
  };
  
  const handleCloseOAuthError = () => {
    setOauthError(null);
    clearError();
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* OAuth Error Modal */}
      {oauthError && (
        <OAuthAccountExistsModal
          isOpen={true}
          onClose={handleCloseOAuthError}
          provider={oauthError.provider}
          email={oauthError.email}
          onGoToLogin={handleGoToLogin}
        />
      )}
      
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{" "}
            <a href="/login" className="font-medium text-primary hover:text-primary/80">
              sign in to your existing account
            </a>
          </p>
        </div>
        
        {error && !error.startsWith('OAUTH_ACCOUNT_EXISTS:') && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Authentication error</AlertTitle>
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        )}
        
        {/* OAuth buttons */}
        <div className="mt-8">
          <OAuthButtons />
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Or register with email</span>
              </div>
            </div>
          </div>
        </div>
        
        <AuthForm
          type="register"
          onSubmit={handleRegister}
          error={null} // We're showing errors above, so don't duplicate
          isLoading={isLoading}
        />
        <div className="mt-6 text-xs text-gray-500 text-center space-x-4">
          <a href="/privacy-policy" className="hover:underline">Privacy Policy</a>
          <span>|</span>
          <a href="/terms-of-service" className="hover:underline">Terms of Service</a>
          <span>|</span>
          <a href="/cookie-policy" className="hover:underline">Cookie Policy</a>
          <span>|</span>
          <a href="/ferpa-compliance" className="hover:underline">FERPA</a>
        </div>
      </div>
    </div>
  );
}
